import { z } from 'zod';

/**
 * Phase 3 — contacts + signup-referral DTOs.
 *
 * Contacts are denormalized at projection time: each row carries the contact
 * user's display info + the sharing context (teams in common, projects in
 * common, whether a DM already exists) so the frontend can render the page
 * without N follow-up fetches.
 */

export const ContactSourceSchema = z.enum(['invited', 'manual', 'shared-team', 'dm']);
export type ContactSource = z.infer<typeof ContactSourceSchema>;

export const ContactSharedTeamSchema = z.object({
  teamId: z.string().uuid(),
  teamSlug: z.string(),
  teamName: z.string(),
});
export type ContactSharedTeam = z.infer<typeof ContactSharedTeamSchema>;

export const ContactSchema = z.object({
  userId: z.string().uuid(),
  username: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  /** How this contact first surfaced in the actor's graph. */
  source: ContactSourceSchema,
  addedAt: z.string().datetime(),
  /** Teams both the actor and the contact belong to.  Empty when source='dm'
   *  or the contact has since left every shared team. */
  sharedTeams: z.array(ContactSharedTeamSchema),
  /** True when an open DM thread already exists between the two. */
  hasOpenDm: z.boolean(),
});
export type ContactProjection = z.infer<typeof ContactSchema>;

export const PendingInvitationSchema = z.object({
  invitationId: z.string().uuid(),
  teamId: z.string().uuid(),
  teamSlug: z.string(),
  teamName: z.string(),
  email: z.string(),
  rolePreset: z.string().nullable(),
  customRoleName: z.string().nullable(),
  invitedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
});
export type PendingInvitationProjection = z.infer<typeof PendingInvitationSchema>;

export const ContactListSchema = z.object({
  contacts: z.array(ContactSchema),
  /** Team invitations the actor has SENT and that are still pending. */
  pendingInvitesSent: z.array(PendingInvitationSchema),
});
export type ContactListProjection = z.infer<typeof ContactListSchema>;

// ── signup referrals ────────────────────────────────────────────────────────

export const SignupReferralSchema = z.object({
  token: z.string(),
  createdAt: z.string().datetime(),
  usedByUserId: z.string().uuid().nullable(),
  usedByName: z.string().nullable(),
  usedAt: z.string().datetime().nullable(),
});
export type SignupReferralProjection = z.infer<typeof SignupReferralSchema>;

export const CreateSignupReferralSchema = z.object({
  // Empty body — server generates the token.
});
export type CreateSignupReferralInput = z.infer<typeof CreateSignupReferralSchema>;
